import React, { useMemo, useState } from "react";
import { Col, Form, Modal, Row, Table } from "react-bootstrap";
import CurrencyFormat from "react-currency-format";
import styled from "styled-components";
import LogButton from "../../../../../../components/atoms/LogButton";
import {
  downloadPurchaseVatBook,
  getPurchaseVatBook,
} from "../../../../../../api/modules";
import {
  errorToast,
  successToast,
} from "../../../../../../services/toastService";

const ModalLogStyled = styled(Modal)`
  opacity: 1;

  .modal-content {
    border-radius: 15px !important;
    padding: 20px;
    border: none;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    border: none;
    padding-left: 0;
    padding-right: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #000;

  &:hover {
    color: #ff0000;
  }
`;

const PeriodInput = styled(Form.Control)`
  background-color: #f6f6f4;
  border: none;
  border-radius: 2px;
  height: 45px;
  color: #6c757d;
`;

const SummaryBox = styled.div`
  background: #f6f6f4;
  border-radius: 8px;
  padding: 10px 14px;
  min-height: 64px;

  small {
    color: #6c757d;
    display: block;
  }

  strong {
    color: #343a40;
  }
`;

const getDefaultPeriod = () => {
  const date = new Date();
  date.setMonth(date.getMonth() - 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}`;
};

const parsePeriod = (period) => {
  const [year, month] = String(period || "").split("-");
  return { year, month };
};

const saveBlob = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

function ModalPurchaseVatBook({ isShow, handleClose }) {
  const [period, setPeriod] = useState(getDefaultPeriod());
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const formattedPeriod = useMemo(() => {
    const { year, month } = parsePeriod(period);
    return month && year ? `${month}/${year}` : "";
  }, [period]);

  const search = async () => {
    const { year, month } = parsePeriod(period);
    if (!year || !month) {
      errorToast("Seleccione un periodo");
      return;
    }

    try {
      setIsLoading(true);
      const response = await getPurchaseVatBook({ year, month });
      if (response?.status && response.status !== 200) {
        errorToast(response.msg || "No se pudo buscar IVA compras");
        return;
      }
      setRows(response.rows || []);
      setTotals(response.totals || null);
      setHasSearched(true);
    } catch (error) {
      console.error("getPurchaseVatBook", error);
      errorToast("No se pudo buscar IVA compras");
    } finally {
      setIsLoading(false);
    }
  };

  const exportFile = async () => {
    const { year, month } = parsePeriod(period);
    if (!year || !month) {
      errorToast("Seleccione un periodo");
      return;
    }

    try {
      setIsLoading(true);
      const response = await downloadPurchaseVatBook({ year, month });
      saveBlob(response.data, `IVA_COMPRAS_${year}_${month}.xlsx`);
      successToast("Planilla descargada");
    } catch (error) {
      console.error("downloadPurchaseVatBook", error);
      errorToast("No se pudo descargar la planilla");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalLogStyled show={isShow} onHide={handleClose} size="xl" centered>
      <Modal.Header>
        <CloseButton type="button" onClick={handleClose}>
          &times;
        </CloseButton>
      </Modal.Header>
      <Modal.Body className="pt-0">
        <h4 className="text-primary font-weight-bold mb-3">IVA compras</h4>
        <Row className="align-items-end">
          <Col md={4}>
            <Form.Label className="font-weight-bold">Periodo</Form.Label>
            <PeriodInput
              type="month"
              value={period}
              onChange={(event) => setPeriod(event.target.value)}
            />
          </Col>
          <Col md={8} className="d-flex justify-content-end mt-3 mt-md-0">
            <LogButton
              title={isLoading ? "Buscando..." : "Buscar"}
              color="primary"
              border="curve"
              className="px-4 mr-2"
              disabled={isLoading}
              onClick={search}
            />
            <LogButton
              title="Exportar Excel"
              color="primary"
              border="curve"
              className="px-4"
              disabled={isLoading || !period}
              onClick={exportFile}
            />
          </Col>
        </Row>

        <Row className="mt-4">
          <Col md={4}>
            <SummaryBox>
              <small>Periodo</small>
              <strong>{formattedPeriod || "-"}</strong>
            </SummaryBox>
          </Col>
          <Col md={4}>
            <SummaryBox>
              <small>Facturas proveedor</small>
              <strong>{totals?.facturas_total || rows.length}</strong>
            </SummaryBox>
          </Col>
          <Col md={4}>
            <SummaryBox>
              <small>Total compras</small>
              <strong>
                <CurrencyFormat
                  value={totals?.importe_total || 0}
                  displayType="text"
                  thousandSeparator
                  decimalScale={2}
                  fixedDecimalScale
                  prefix="$"
                />
              </strong>
            </SummaryBox>
          </Col>
        </Row>

        <div className="mt-4" style={{ maxHeight: 420, overflow: "auto" }}>
          <Table bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Proveedor</th>
                <th>CUIT</th>
                <th>Factura proveedor</th>
                <th>Proformas</th>
                <th className="text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${row["Factura proveedor"]}-${row.CUIT}-${index}`}>
                  <td>{row.Fecha}</td>
                  <td>{row.Proveedor}</td>
                  <td>{row.CUIT}</td>
                  <td>{row["Factura proveedor"]}</td>
                  <td>{row.Proformas}</td>
                  <td className="text-right">
                    <CurrencyFormat
                      value={row.Total || 0}
                      displayType="text"
                      thousandSeparator
                      decimalScale={2}
                      fixedDecimalScale
                      prefix="$"
                    />
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    {hasSearched
                      ? "No hay facturas proveedor recibidas para el periodo."
                      : "Seleccione un periodo y busque los comprobantes."}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </Modal.Body>
    </ModalLogStyled>
  );
}

export default ModalPurchaseVatBook;
